import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { Subject, of } from "rxjs";
import { finalize, switchMap, takeUntil } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { ToastrTranslatedService } from "../../shared/toastr-translated.service";
import { ConfirmationDialogComponent } from "../../shared/confirmation-dialog/confirmation-dialog.component";
import { ConfirmationDialogParams } from "../../shared/confirmation-dialog/confirmation-dialog.interface";
import type {
  CardOrder,
  CardOrderFulfillmentTimelineStep,
} from "../card-orders.interface";
import { CardOrdersService } from "../card-orders.service";
import {
  formatUpsAccessPointAddress,
  formatUpsAccessPointName,
  formatUpsAccessPointOpeningHours as localizeUpsAccessPointOpeningHours,
  formatShippingAddressLines,
  getCountryDisplayName as getLocalizedCountryDisplayName,
  getCountryFlagUrl,
  getDeliverySpeedLabelKey,
  getFulfillmentStageLabelKey,
  getShipmentCarrierLabelKey,
  getShipmentMethodLabelKey,
  getOrderTrackingNumber,
  getOrderUpsPackages,
  getOrderUpsShipmentDescription,
  getOrderUpsTracking,
  getVisibleFulfillmentTimelineSteps,
  hasOrderExternalShipment,
  hasOrderUpsTracking,
  isOrderUpsDelivered,
} from "../card-order.util";

@Component({
  selector: "app-card-order-detail",
  templateUrl: "./card-order-detail.component.html",
  styleUrls: ["./card-order-detail.component.scss"],
  standalone: false,
})
export class CardOrderDetailComponent implements OnInit, OnDestroy {
  order: CardOrder | null = null;
  loading = true;
  cancelLoading = false;
  approveLoading = false;
  readonly formatUpsAccessPointName = formatUpsAccessPointName;
  readonly formatUpsAccessPointAddress = formatUpsAccessPointAddress;
  readonly formatShippingAddressLines = formatShippingAddressLines;
  readonly getCountryFlagUrl = getCountryFlagUrl;
  readonly getFulfillmentStageLabelKey = getFulfillmentStageLabelKey;
  readonly getShipmentMethodLabelKey = getShipmentMethodLabelKey;
  readonly getDeliverySpeedLabelKey = getDeliverySpeedLabelKey;
  readonly getShipmentCarrierLabelKey = getShipmentCarrierLabelKey;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly ordersService: CardOrdersService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly toastr: ToastrTranslatedService,
    private readonly translate: TranslateService,
    private readonly dialog: MatDialog,
  ) {}

  getCountryDisplayName(alpha2Code: string | null | undefined): string {
    return getLocalizedCountryDisplayName(
      alpha2Code,
      this.translate.currentLang,
    );
  }

  formatUpsAccessPointOpeningHours(
    value: string | null | undefined,
  ): string | null {
    return localizeUpsAccessPointOpeningHours(
      value,
      (key) => this.translate.instant(key) as string,
    );
  }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        switchMap((params) => {
          const orderId = params.get("printCardOrderId")?.trim();
          if (!orderId) {
            void this.router.navigate(["/card-orders"]);
            this.toastr.error("cardOrders.loadFailed");
            return of(null);
          }
          this.loading = true;
          return this.ordersService.getOrder(orderId);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (order) => {
          if (!order) {
            this.loading = false;
            return;
          }
          this.order = {
            ...order,
            printCardOrderId: order.printCardOrderId?.trim() ?? "",
          };
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.toastr.error("cardOrders.loadFailed");
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get visibleFulfillmentSteps(): CardOrderFulfillmentTimelineStep[] {
    return getVisibleFulfillmentTimelineSteps(
      this.order?.fulfillment?.timeline,
    );
  }

  get hasUpsTracking(): boolean {
    return hasOrderUpsTracking(this.order);
  }

  get hasExternalShipment(): boolean {
    return hasOrderExternalShipment(this.order);
  }

  get orderUpsPackages() {
    return getOrderUpsPackages(this.order);
  }

  get orderUpsShipmentDescription(): string | null {
    return getOrderUpsShipmentDescription(this.order);
  }

  get upsTrackingNumber(): string | null {
    return getOrderTrackingNumber(this.order ?? {});
  }

  get upsTrackingStatus(): string | null {
    return (
      getOrderUpsTracking(this.order)?.status?.trim() ||
      this.order?.ups?.trackingStatus?.trim() ||
      null
    );
  }

  get upsTrackingActivities() {
    return getOrderUpsTracking(this.order)?.activities ?? [];
  }

  get isUpsDelivered(): boolean {
    return isOrderUpsDelivered(this.order, getOrderUpsTracking(this.order));
  }

  get canCancelOrder(): boolean {
    return Boolean(this.order?.fulfillment?.actions?.canCancelOrder);
  }

  get canApproveOrder(): boolean {
    return Boolean(this.order?.fulfillment?.actions?.canApproveOrder);
  }

  approveOrder(): void {
    if (!this.order || !this.canApproveOrder) {
      return;
    }

    const orderId = this.order.printCardOrderId?.trim();
    if (!orderId) {
      return;
    }

    const data: ConfirmationDialogParams = {
      title: "cardOrders.approveOrderTitle",
      body: "cardOrders.approveOrderBody",
      confirmButton: "cardOrders.approveOrderConfirm",
    };

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: "440px",
      data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.approveLoading = true;
        this.ordersService
          .approveOrder(orderId)
          .pipe(
            finalize(() => {
              this.approveLoading = false;
            }),
            takeUntil(this.destroy$),
          )
          .subscribe({
            next: (order) => {
              this.order = {
                ...order,
                printCardOrderId: order.printCardOrderId?.trim() ?? "",
              };
              this.toastr.success("cardOrders.approveOrderSuccess");
            },
            error: () => {
              this.toastr.error("cardOrders.approveOrderFailed");
            },
          });
      });
  }

  cancelOrder(): void {
    if (!this.order || !this.canCancelOrder) {
      return;
    }

    const orderId = this.order.printCardOrderId?.trim();
    if (!orderId) {
      return;
    }

    const data: ConfirmationDialogParams = {
      title: "cardOrders.cancelOrderTitle",
      body: "cardOrders.cancelOrderBody",
      confirmButton: "cardOrders.cancelOrderConfirm",
    };

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: "440px",
      data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.cancelLoading = true;
        this.ordersService
          .cancelOrder(orderId)
          .pipe(
            finalize(() => {
              this.cancelLoading = false;
            }),
            takeUntil(this.destroy$),
          )
          .subscribe({
            next: (order) => {
              this.order = {
                ...order,
                printCardOrderId: order.printCardOrderId?.trim() ?? "",
              };
              this.toastr.success("cardOrders.cancelOrderSuccess");
            },
            error: () => {
              this.toastr.error("cardOrders.cancelOrderFailed");
            },
          });
      });
  }
}
